<template>
<div>
  <div class="container-input">
    <div class="input-header">
      <strong>Etapa {{ nStepsBefore }} de {{ nRootSteps }} - Selecione {{ onlySameDate ? 'o' : 'data e' }} horário da atividade {{ value.nome }}</strong>
    </div>
    <div class="input-body">
      <div v-if="value.initial_dates.length > 0">
        <div v-if="!onlySameDate">
          <input-data-calendario
            v-if="value.use_calendario"
            v-model="selectedDate"
            :passeio-id="value.id"
            :initial-available-days="JSON.stringify(value.initial_dates)"
            :calendar-size="calendarSizeParsed"
            :start-opened="true"
            @on-open-change="heightChange" />
          <input-data-lista
            v-else
            v-model="selectedDate"
            :passeio-id="value.id"
            :available-days="JSON.stringify(value.initial_dates)"
            :return-days="JSON.stringify(value.initial_return_dates)"
            :primary-color="primaryColor" />
        </div>
        <div v-else>
          <div class="container-date-selected">
            <div style="font-size: 14px;">
              <i class="fa-solid fa-calendar-days" />&nbsp;&nbsp;
              <span style="color: #757575;">{{ selectedDateDescription }}</span>
            </div>
          </div>
        </div>
        <div
          v-show="loadingAvailability"
          style="text-align: center; margin-top: 10px;">
          <i class="fa-regular fa-hourglass" />
        </div>
        <input-horario
          v-show="!loadingAvailability && selectedDate"
          :value="selectedAvailabilityId"
          style="margin-top: 20px;"
          :horarios="horarios"
          :selected-quantities="selectedAgeRanges"
          :primary-color="primaryColor"
          :show-remaining-vacancies="showRemainingVacancies"
          :passeio-tipo="value.tipo_passeio"
          :titulo-atividade="tituloAtividade"
          :genero-titulo-atividade="generoTituloAtividade"
          :is-mobile="isMobile"
          @max-vagas-changed="v => maxVagas = v"
          @hora-changed="inputHora" />
        <div
          v-if="quantidadePessoas > maxVagas && !loadingAvailability && selectedDate"
          class="alert alert-danger"
          role="alert">
          Não existem vagas suficientes para a quantidade de pessoas selecionadas.
        </div>
        <div
          v-if="scheduleErrorMessage"
          class="alert alert-danger"
          role="alert">
          {{ scheduleErrorMessage }}
        </div>
      </div>
      <div v-else>
        <i>Nenhuma data disponível</i>
      </div>
    </div>

    <div :class="{'optionals-margins-mobile': isMobile}">
      <div
        v-show="loadingOptionals"
        style="text-align: center; margin-top: 10px;">
        <i class="fa-regular fa-hourglass" />
      </div>
      <input-opcionais
        v-show="selectedDate"
        v-model="selectedOptionals"
        style="margin-top: 20px;"
        :selected-date="selectedDate"
        :max="nPessoas"
        :primary-color="primaryColor"
        :mobile-mode="isMobile"
        :passeio-id="value.id"
        :moeda="moeda"
        @is-loading="v => loadingOptionals = v"
        @optionals-availability-change="v => hasOptionals = v" />
    </div>
  </div>
  <nav-buttons
    :style="{'margin-top': isMobile ? '0px' : '20px'}"
    :disable-previus="false"
    :disable-next="disableGoNext"
    :loading="loadingOptionals"
    :is-mobile="isMobile"
    :sub-total="subTotal"
    :moeda="moeda"
    @next="next"
    @previus="previus" />
</div>
</template>

<script>
import NavButtons from './NavButtons.vue';
import InputDataCalendario from '../components/InputDataCalendario.vue';
import InputDataLista from '../components/InputDataLista.vue';
import api from '@api/store/atividades';
import InputHorario from '../components/InputHorario.vue';
import InputOpcionais from '../components/InputOpcionais.vue';
import moment from 'moment';

const dateToString = function(date) {
    const mes = date.toLocaleDateString('pt-BR', { month: '2-digit' });
    const dia = date.toLocaleDateString('pt-BR', { day: '2-digit' });
    const ano = date.toLocaleDateString('pt-BR', { year: 'numeric' });
    return ano + '-' + mes + '-' + dia;
};

export default {
    components: { NavButtons, InputDataCalendario, InputDataLista, InputHorario, InputOpcionais },
    props: {
        value: {
            type: Object,
            default: () => {},
            required: true
        },
        active: {
            type: Boolean,
            default: () => false,
            required: false
        },
        primaryColor: {
            type: String,
            default: () => '#FF0000',
            required: true
        },
        moeda: {
            type: String,
            default: () => 'BRL',
            required: true
        },
        isMobile: {
            type: Boolean,
            default: () => false,
            required: false,
        },
        calendarSize: {
            type: Number,
            default: () => 2,
            required: true
        },
        nPessoas: {
            type: Number,
            default: () => 2,
            required: true
        },
        showRemainingVacancies: {
            type: Boolean,
            default: () => false,
            required: true
        },
        tituloAtividade: {
            type: String,
            default: () => 'Atividade',
            required: false
        },
        generoTituloAtividade: {
            type: String,
            default: () => 'f',
            required: false
        },
        selectedAgeRanges: {
            type: Array,
            default: () => [],
            required: false
        },
        onlySameDate: {
            type: Boolean,
            default: () => false,
            required: true
        },
        subTotal: {
            type: Number,
            default: () => 0,
            required: true
        },
        nStepsBefore: {
            type: Number,
            default: () => 0,
            required: true
        },
        nRootSteps: {
            type: Number,
            default: () => 0,
            required: true
        },
        scheduleConflict: {
            validator: (val) => val instanceof Object || val === null,
            default: () => null,
            required: true
        }
    },
    emits: ['input', 'dom-height-change', 'next', 'previus'],
    data() {
        return {
            horarios: [],
            loadingAvailability: false,
            maxVagas: 0,
            loadingOptionals: false,
            hasOptionals: false,
        };
    },
    computed: {
        selectedDate: {
            get() {
                return this.value.selectedDay;
            },
            set(newValue) {
                const input = JSON.parse(JSON.stringify(this.value));
                input.selectedDay = newValue;
                input.selectedAvailability = null;
                this.$emit('input', input);
            }
        },
        scheduleErrorMessage() {
            if(!this.scheduleConflict) {
                return null;
            }
            const startSecondFormated = this.scheduleConflict.second.startMoment.format('HH:mm');
            const endFirstFormated = this.scheduleConflict.first.endMoment.format('HH:mm');
            let comesFirst = this.value.id == this.scheduleConflict.first.id;
            let r = 'O horário selecionado conflita com o horário da atividade \''
                + (comesFirst ? this.scheduleConflict.second.nome : this.scheduleConflict.first.nome) + '\' '
                + (
                    comesFirst
                        ? ' que começa ' + startSecondFormated + 'h, antes desta terminar(' + endFirstFormated + ').'
                        : ' que termina ' + endFirstFormated + 'h, depois desta começar.'
                );
            return r;
        },
        calendarSizeParsed() {
            if(this.isMobile) {
                return 1;
            }
            return this.calendarSize;
        },
        selectedDateDescription() {
            if(!this.selectedDate) {
                return 'Nenhuma data';
            }
            let mes = this.selectedDate.toLocaleDateString('pt-BR', { month: 'long' });
            mes = mes[0].toUpperCase() + mes.substring(1);
            const dia = this.selectedDate.toLocaleDateString('pt-BR', { day: 'numeric' });
            const year = this.selectedDate.toLocaleDateString('pt-BR', { year: 'numeric' });
            const diaSemana = this.selectedDate.toLocaleDateString('pt-BR', { weekday: 'long' });
            return dia + ' de ' + mes + ' | ' + year + ' (' + diaSemana + ')';
        },
        selectedDateStr() {
            if(!this.selectedDate) {
                return '';
            }
            return this.selectedDate.toLocaleDateString('pt-BR', { year: 'numeric', month: '2-digit', day: '2-digit' });
        },
        selectedAvailabilityId() {
            if(!this.value.selectedAvailability) {
                return null;
            }
            return this.value.selectedAvailability.id;
        },
        selectedOptionals: {
            get() {
                return this.value.selectedOptionals;
            },
            set(newValue) {
                const input = JSON.parse(JSON.stringify(this.value));
                input.selectedDay = this.value.selectedDay;
                input.selectedOptionals = newValue;
                this.$emit('input', input);
            }
        },
        nSteps() {
            return this.hasOptionals ? 2 : 1;
        },
        disableGoNext() {
            return !this.selectedDate || !this.selectedAvailabilityId || this.loadingOptionals;
        },
        quantidadePessoas() {
            let pessoas = 0;
            this.selectedAgeRanges.forEach(faixa => {
                pessoas += faixa.quantidade;
            });
            return pessoas;
        }
    },
    watch: {
        selectedDateStr() {
            this.syncHorarios();
        },
        selectedAvailabilityId() {
            this.heightChange();
        },
        loadingAvailability() {
            this.heightChange();
        },
        loadingOptionals() {
            this.heightChange();
        },
        scheduleErrorMessage() {
            this.heightChange();
        },
    },
    methods: {
        next() {
            this.$emit('next');
        },
        previus() {
            this.$emit('previus');
        },
        heightChange() {
            this.$nextTick(() => {
                this.$emit('dom-height-change');
            });
        },
        generateMoment(horario) {
            const hora = horario.hora_de ? horario.hora_de : '00:00:00';
            return moment(horario.date + ' ' + hora, 'YYYY-MM-DD HH:mm:ss');
        },
        inputHora(hora) {
            const quantidadeDisponivel = hora ? (hora.quantidade - hora.count) : 0;
            const input = JSON.parse(JSON.stringify(this.value));
            input.selectedDay = this.value.selectedDay;
            input.selectedAvailability = quantidadeDisponivel >= this.quantidadePessoas ? hora : null;
            this.$emit('input', input);
        },
        async syncHorarios() {
            if(!this.selectedDate || this.loadingAvailability) {
                return;
            }
            this.loadingAvailability = true;
            const data = dateToString(this.selectedDate);
            try {
                const r = await api.listSchedules(this.value.id, data);
                if(typeof r !== 'object' || Array.isArray(r) || r === null) {
                    return;
                }
                const chaves = Object.keys(r);
                const horarios = [];
                chaves.forEach(chave => {
                    horarios.push(r[chave]);
                });
                const newHorarios = horarios;
                newHorarios.sort((a, b) => {
                    const aMoment = this.generateMoment(a);
                    const bMoment = this.generateMoment(b);
                    if(aMoment.isBefore(bMoment)) {
                        return -1;
                    }
                    if(aMoment.isAfter(bMoment)) {
                        return 1;
                    }
                    return 0;
                });
                this.horarios = newHorarios;
            } catch(err) {
                //Empty
            } finally {
                this.loadingAvailability = false;
            }
        },
    }
};
</script>

<style scoped>
.container-input .input-header {
    font-size: 14px;
    margin-top: 10px;
    margin-bottom: 20px;
}
.container-input .input-body {
    margin-top: 15px;
}

.container-date-selected {
    border: 1px solid #707070;
    border-radius: 5px;
    padding: 15px;
}

.optionals-margins-mobile {
    margin-left: 20px;
    margin-right: 20px;
}

@media (max-width: 770px) {
    .container-input .input-header {
        margin-left: -10px;
        width: calc(100% + 20px);
        padding-left: 10px;
    }
}
</style>
